import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hh_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [authing, setAuthing] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems([]);
  }, []);

  const fetchItems = useCallback(async (tok) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/submissions`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setItems(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Your session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Could not load submissions.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) fetchItems(token);
  }, [token, fetchItems]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthing(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      toast.error(
        err.response?.status === 401
          ? "Invalid email or password."
          : "Something went wrong. Please try again."
      );
    } finally {
      setAuthing(false);
    }
  };

  const shown = items.filter((i) => filter === "all" || i.type === filter);

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-6">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="cozy-card w-full max-w-sm p-8"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-seafoam">
            <Lock className="h-6 w-6 text-teal" />
          </div>
          <h1 className="mt-5 text-3xl">Admin sign in</h1>
          <p className="mt-1 text-sm text-bodytext/70">
            Harbor Home submissions — team only.
          </p>
          <div className="mt-6 space-y-4">
            <div>
              <Label className="mb-1.5 block text-sm font-medium text-navy">Email</Label>
              <Input
                data-testid="admin-email"
                type="email"
                value={creds.email}
                onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                placeholder="you@email.com"
                required
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium text-navy">Password</Label>
              <Input
                data-testid="admin-password"
                type="password"
                value={creds.password}
                onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                placeholder="••••••••"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={authing}
            className="btn-teal mt-6 w-full disabled:opacity-60"
          >
            {authing ? "Signing in…" : "Sign In"}
          </button>
          <Link to="/" className="mt-4 block text-center text-sm text-teal">
            Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream px-6 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl">Submissions</h1>
            <p className="mt-1 text-sm text-bodytext/70">
              Quote, walkthrough, application &amp; contact leads.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/" className="btn-outline-navy" data-testid="admin-home-link">
              Back to site
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="btn-outline-navy">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {["all", "walkthrough", "quote", "application", "contact"].map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                filter === f ? "bg-teal text-white" : "bg-seafoam text-navy"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-10 text-bodytext">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="mt-10 text-bodytext" data-testid="admin-empty">
            No submissions yet.
          </p>
        ) : (
          <div className="mt-6 space-y-4" data-testid="admin-list">
            {shown.map((s) => (
              <div key={s.id} className="cozy-card p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-seafoam px-3 py-1 text-xs font-semibold uppercase text-navy">
                      {s.type}
                    </span>
                    <h3 className="text-xl">{s.name}</h3>
                  </div>
                  <span className="text-xs text-bodytext/60">
                    {new Date(s.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="mt-3 grid gap-x-8 gap-y-1 text-sm text-bodytext sm:grid-cols-2">
                  <p><strong className="text-navy">Email:</strong> {s.email}</p>
                  <p><strong className="text-navy">Phone:</strong> {s.phone}</p>
                  {s.town && <p><strong className="text-navy">Town:</strong> {s.town}</p>}
                  {s.service && <p><strong className="text-navy">Service:</strong> {s.service}</p>}
                  {s.support_type && <p><strong className="text-navy">Support:</strong> {s.support_type}</p>}
                  {s.home_size && <p><strong className="text-navy">Home size:</strong> {s.home_size}</p>}
                  {s.timeline && <p><strong className="text-navy">Timeline:</strong> {s.timeline}</p>}
                  {s.experience && <p><strong className="text-navy">Experience:</strong> {s.experience}</p>}
                  {s.property_details && <p><strong className="text-navy">Property:</strong> {s.property_details}</p>}
                  {s.referral_source && <p><strong className="text-navy">Heard via:</strong> {s.referral_source}</p>}
                </div>
                {s.biggest_overwhelm && (
                  <p className="mt-3 rounded-[8px] bg-seafoam/60 p-3 text-sm text-bodytext">
                    <strong className="text-navy">Overwhelm:</strong> {s.biggest_overwhelm}
                  </p>
                )}
                {s.message && (
                  <p className="mt-3 rounded-[8px] bg-seafoam/60 p-3 text-sm text-bodytext">
                    {s.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
